/**
 * Voice Library
 * ===========================================================================
 * Every designed voice this Book Creator owns, in one place, with an audition clip for each.
 * Replaces a static placeholder that listed four books and played no audio at all.
 *
 * WHAT THE REAL DATA MADE THIS PAGE BE (measured 2026-08-02; each point changed the design):
 *
 *   972 voices — but only 496 still EXIST on ElevenLabs. Slot reclamation deletes the voice
 *   and the graph keeps the pointer, so a library listing all 972 offers renders that will
 *   fail. Availability is shown per row, and "ready to use only" is the default filter.
 *
 *   0 live voices are unattached. Every working voice already speaks for someone, so this is a
 *   DIRECTORY OF VOICES IN USE rather than a pool of spares. Each row names who currently
 *   speaks with it, because assigning one means deliberately sharing it.
 *
 *   285 characters hold those 972 voices, and 172 names have more than one. Some are one
 *   character recast repeatedly; others are different people sharing a label — an old man in
 *   one book is not the old man in another. No metadata separates those two cases. Only
 *   listening does, which is why audition is the primary action and nothing here merges.
 *
 * Read-only by design: assignment belongs to the Casting Studio, where the guards live.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Segment, Header, Icon, Input, Dropdown, Checkbox, Label, Loader, Message, Button, Statistic,
} from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';

const unwrap = (resp) =>
  (resp && resp.data && resp.data.data !== undefined ? resp.data.data : resp && resp.data);

// The app has no single declared panel width — the CSS max-widths are responsive breakpoints
// (768/980/1080) rather than a container measure. 1024 is what the operator reads the rest of
// the app at, so it is the house width until there is a real token to point at.
const PANEL_MAX_WIDTH = 1024;

const GENDERS = [
  { key: 'all', value: 'all', text: 'Any gender' },
  { key: 'female', value: 'female', text: 'Female' },
  { key: 'male', value: 'male', text: 'Male' },
  { key: 'neutral', value: 'neutral', text: 'Neutral' },
  { key: 'unknown', value: 'unknown', text: 'Unspecified' },
];

const SORTS = [
  { key: 'recent', value: 'recent', text: 'Newest first' },
  { key: 'name', value: 'name', text: 'Character A–Z' },
  { key: 'most', value: 'most', text: 'Most voices first' },
];

function VoiceLibrary() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [gender, setGender] = useState('all');
  const [liveOnly, setLiveOnly] = useState(true);
  const [multiOnly, setMultiOnly] = useState(false);
  const [sort, setSort] = useState('recent');

  const [playing, setPlaying] = useState(null);   // uuid of the row currently sounding
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/voice-library');
        if (!cancelled) setData(unwrap(resp));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Could not load the voice library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ONE shared <audio>. A player per row is how you end up with four voices talking at once,
  // which is precisely the judgement this page exists to support.
  const audition = useCallback((voice) => {
    const el = audioRef.current;
    if (!el || !voice.auditionUrl) return;
    if (playing === voice.uuid) { el.pause(); setPlaying(null); return; }
    el.src = voice.auditionUrl;
    el.play().then(() => setPlaying(voice.uuid)).catch(() => setPlaying(null));
  }, [playing]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const done = () => setPlaying(null);
    el.addEventListener('ended', done);
    return () => el.removeEventListener('ended', done);
  }, []);

  // Grouped by character, because that is the unit the operator thinks in — and because a name
  // holding nine voices is the most useful single thing this page can surface.
  const groups = useMemo(() => {
    if (!data || !data.voices) return [];
    const needle = q.trim().toLowerCase();
    const byKey = new Map();

    for (const v of data.voices) {
      if (liveOnly && v.live === false) continue;
      if (gender !== 'all' && (v.gender || 'unknown') !== gender) continue;
      if (needle) {
        const hay = `${v.characterKey} ${v.name} ${v.role || ''} ${(v.books || []).join(' ')} ${(v.usedBy || []).join(' ')}`.toLowerCase();
        if (!hay.includes(needle)) continue;
      }
      if (!byKey.has(v.characterKey)) byKey.set(v.characterKey, []);
      byKey.get(v.characterKey).push(v);
    }

    let out = [...byKey.entries()].map(([key, voices]) => ({ key, voices }));
    if (multiOnly) out = out.filter((g) => g.voices.length > 1);

    if (sort === 'name') out.sort((a, b) => a.key.localeCompare(b.key));
    else if (sort === 'most') out.sort((a, b) => b.voices.length - a.voices.length || a.key.localeCompare(b.key));
    else out.sort((a, b) => String(b.voices[0].createdAt || '').localeCompare(String(a.voices[0].createdAt || '')));
    return out;
  }, [data, q, gender, liveOnly, multiOnly, sort]);

  const shownVoices = useMemo(() => groups.reduce((n, g) => n + g.voices.length, 0), [groups]);

  if (loading) {
    return <Segment basic padded="very" textAlign="center"><Loader active inline>Loading your voices…</Loader></Segment>;
  }
  if (error) return <Message negative header="Voice library unavailable" content={error} />;

  const s = (data && data.summary) || {};

  return (
    // Constrained to the panel width the rest of the app reads at. Semantic's <Container> is
    // the usual vehicle, but its max-width steps by breakpoint (~1127px on desktop); pinning
    // it here keeps the row layout — play button, descriptors, current speakers — at a
    // consistent measure instead of stretching on a wide monitor.
    <div style={{ padding: '1rem 0', maxWidth: PANEL_MAX_WIDTH, margin: '0 auto' }}>
      <audio ref={audioRef} style={{ display: 'none' }} />

      <Header as="h2">
        <Icon name="microphone" />
        <Header.Content>
          Voice Library
          <Header.Subheader>Every voice you have designed, with an audition for each.</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Statistic.Group size="mini" widths={5}>
          <Statistic>
            <Statistic.Value>{s.characters ?? '—'}</Statistic.Value>
            <Statistic.Label>characters</Statistic.Label>
          </Statistic>
          <Statistic color="green">
            <Statistic.Value>{s.live ?? '—'}</Statistic.Value>
            <Statistic.Label>voices ready</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{s.dead ?? '—'}</Statistic.Value>
            <Statistic.Label>no longer available</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{s.inUse ?? '—'}</Statistic.Value>
            <Statistic.Label>in use</Statistic.Label>
          </Statistic>
          <Statistic color={s.charactersWithSeveralVoices ? 'orange' : undefined}>
            <Statistic.Value>{s.charactersWithSeveralVoices ?? '—'}</Statistic.Value>
            <Statistic.Label>have several voices</Statistic.Label>
          </Statistic>
        </Statistic.Group>

        {s.livenessKnown === false && (
          <Message warning size="tiny" style={{ marginTop: '1rem' }}
            content="Could not reach ElevenLabs, so availability is unknown on this load — every voice is listed." />
        )}
        {s.charactersWithSeveralVoices > 0 && (
          <Message info size="tiny" style={{ marginTop: '1rem' }}>
            <Icon name="info circle" />
            {s.charactersWithSeveralVoices} characters hold more than one voice. Some are the same person
            recast; others are different people who happen to share a name — <strong>an old man in one book
            is not the old man in another</strong>. Listen before treating them as the same.
          </Message>
        )}
      </Segment>

      <Segment>
        <Input
          icon="search" iconPosition="left" placeholder="Character, role, book…"
          value={q} onChange={(e, d) => setQ(d.value)}
          style={{ minWidth: 280, marginRight: '1rem' }}
        />
        <Dropdown selection compact options={GENDERS} value={gender}
          onChange={(e, d) => setGender(d.value)} style={{ marginRight: '1rem' }} />
        <Dropdown selection compact options={SORTS} value={sort}
          onChange={(e, d) => setSort(d.value)} style={{ marginRight: '1.5rem' }} />
        <Checkbox toggle label="Ready to use only" checked={liveOnly}
          onChange={(e, d) => setLiveOnly(d.checked)} style={{ marginRight: '1.5rem' }} />
        <Checkbox toggle label="Only characters with several voices" checked={multiOnly}
          onChange={(e, d) => setMultiOnly(d.checked)} />
        <div style={{ marginTop: '0.75rem', color: '#888', fontSize: 12 }}>
          {groups.length} character{groups.length === 1 ? '' : 's'} · {shownVoices} voice{shownVoices === 1 ? '' : 's'} shown
        </div>
      </Segment>

      {groups.length === 0 && (
        <Segment basic textAlign="center" style={{ color: '#888' }}>Nothing matches those filters.</Segment>
      )}

      {groups.map((g) => (
        <Segment key={g.key} style={{ padding: '0.9rem 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.6rem' }}>
            <span style={{ fontWeight: 600, fontSize: '1.05rem', textTransform: 'capitalize' }}>{g.key}</span>
            {g.voices.length > 1 && (
              <Label size="tiny" color="orange">{g.voices.length} voices — audition to compare</Label>
            )}
          </div>

          {g.voices.map((v) => (
            <div
              key={v.uuid}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.45rem 0', borderTop: '1px solid #f0f0f0',
              }}
            >
              <Button
                icon circular size="small"
                color={playing === v.uuid ? 'teal' : undefined}
                disabled={!v.auditionUrl}
                title={v.auditionUrl ? 'Audition this voice' : 'No audition clip stored'}
                onClick={() => audition(v)}
              >
                <Icon name={playing === v.uuid ? 'pause' : 'play'} />
              </Button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>
                  {v.name}
                  {v.live === false && <Label size="mini" style={{ marginLeft: 6 }}>no longer available</Label>}
                  {v.autoGenerated && <Label size="mini" basic style={{ marginLeft: 6 }}>auto-cast</Label>}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                  {[v.gender, v.age, v.role].filter(Boolean).join(' · ') || 'no descriptors'}
                  {v.books && v.books.length
                    ? ` — ${v.books[0]}${v.books.length > 1 ? ` +${v.books.length - 1}` : ''}` : ''}
                  {v.createdAt ? ` · ${String(v.createdAt).slice(0, 10)}` : ''}
                </div>
              </div>

              <div style={{ fontSize: 11, color: '#777', maxWidth: 220, textAlign: 'right' }}>
                {v.usedBy && v.usedBy.length ? (
                  <>speaks for <strong>{v.usedBy.slice(0, 2).join(', ')}</strong>
                    {v.usedBy.length > 2 ? ` +${v.usedBy.length - 2}` : ''}</>
                ) : <span style={{ color: '#bbb' }}>not assigned</span>}
              </div>
            </div>
          ))}
        </Segment>
      ))}
    </div>
  );
}

export default VoiceLibrary;
